import React, { useState, useEffect, useContext } from 'react';
import {
    Receipt,
    Schedule,
    Send,
    Link,
    WhatsApp,
    Email,
    Sms,
    ContentCopy,
    Check,
    Add,
    Search,
    Refresh,
    Close
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import CashierPaymentModal from '../payment/CashierPaymentModal';
import { fetchPaymentRequests } from '../../services/paymentRequestService';
import { useAuth } from '../../contexts/AuthContext';
import { XhrContext } from '../../contexts/XhrContext';
import moment from 'moment';

const PaymentRequestPanel = ({ caissierData, terminal }) => {
    const { selectedOrganization, user } = useAuth();
    const { getRequest } = useContext(XhrContext);
    
    // États pour la liste
    const [paymentRequests, setPaymentRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [pge, setPage] = useState(1);
    const [lmt] = useState(10);
    const [sts, setStatus] = useState('all');
    const [slug, setSlug] = useState('');
    const [prevPage, setPrevPage] = useState(1);
    const [nextPage, setNextPage] = useState(1);
    
    // États pour la création
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [amount, setAmount] = useState('');
    const [currency, setCurrency] = useState('USD');
    const [description, setDescription] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [qrValue, setQrValue] = useState('');
    const [paymentLink, setPaymentLink] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [copied, setCopied] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);

    // Charger les demandes de paiement (logique du mobile)
    useEffect(() => {
        if (selectedOrganization?.id) {
            loadPaymentRequests();
        }
    }, [selectedOrganization?.id, sts, slug]);

    const loadPaymentRequests = async () => {
        try {
            setLoading(true);
            const params = {
                sts: sts,
                org: selectedOrganization.id,
                emt: user?.id || 'all', // Pour caissier, filtrer par user.id
                cmr: 'all',
                tag: terminal?.tag || 'all',
                slg: slug,
                pge: pge,
                lmt: lmt
            };
            
            const data = await fetchPaymentRequests(params);
            
            if (data && data?.contents?.length > 0) {
                setPaymentRequests(data.contents || []);
                setPrevPage(data.previousPage || 1);
                setNextPage(data.nextPage || 1);
            } else {
                setPaymentRequests([]);
                setPrevPage(1);
                setNextPage(1);
                setPage(1);
            }
        } catch (error) {
            console.error('Error loading payment requests:', error);
            setPaymentRequests([]);
        } finally {
            setLoading(false);
        }
    };

    const getStatusStyle = (status) => {
        const normalizedStatus = status?.toLowerCase() || '';
        switch (normalizedStatus) {
            case 'paid': return 'bg-green-100 text-green-800 border-green-200';
            case 'pending': return 'bg-amber-100 text-amber-800 border-amber-200';
            case 'cashed': return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'expired': return 'bg-red-100 text-red-800 border-red-200';
            default: return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getStatusTranslate = (status) => {
        switch (status) {
            case 'all': return 'Tous';
            case 'paid': return 'Payée';
            case 'pending': return 'En attente';
            case 'cashed': return 'Encaissée';
            case 'expired': return 'Expirée';
            default: return 'En attente';
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        return moment(dateString).format('DD.MM.YYYY HH:mm').replace(':', 'h');
    };

    const formatCurrency = (value, curr = 'USD') => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: curr
        }).format(value);
    };

    const generatePaymentRequest = () => {
        if (!amount || amount <= 0) {
            alert('Veuillez entrer un montant valide');
            return;
        }
        setShowPaymentModal(true);
    };

    const resetForm = () => {
        setAmount('');
        setDescription('');
        setPhoneNumber('');
        setQrValue('');
        setPaymentLink('');
        setCopied(false);
        setShowCreateForm(false);
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    // Si on affiche le formulaire de création
    if (showCreateForm) {
        return (
            <div className="space-y-6">
                <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 p-8">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                            <div className="p-3 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl">
                                <Send className="text-white text-2xl" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900">Demander un Paiement</h2>
                                <p className="text-gray-600">Envoyez une demande de paiement</p>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowCreateForm(false)}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-xl transition-all"
                        >
                            <Close />
                        </button>
                    </div>

                    <div className="mb-6">
                        <label className="block text-sm font-semibold text-gray-700 mb-3">Montant</label>
                        <div className="flex space-x-3">
                            <select
                                value={currency}
                                onChange={(e) => setCurrency(e.target.value)}
                                className="bg-gray-100 border-0 rounded-2xl px-4 py-3 font-semibold focus:ring-2 focus:ring-amber-500 focus:outline-none"
                            >
                                <option value="USD">USD</option>
                                <option value="CDF">CDF</option>
                            </select>
                            <input
                                type="number"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder="0.00"
                                className="flex-1 bg-gray-50 border-0 rounded-2xl px-4 py-3 text-2xl font-bold text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:outline-none"
                            />
                        </div>
                    </div>

                    <div className="mb-6">
                        <label className="block text-sm font-semibold text-gray-700 mb-3">Numéro du client *</label>
                        <input
                            type="tel"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            placeholder="+243 97 123 4567"
                            className="w-full bg-gray-50 border-0 rounded-2xl px-4 py-3 text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:outline-none"
                        />
                    </div>

                    <div className="mb-8">
                        <label className="block text-sm font-semibold text-gray-700 mb-3">Description (optionnel)</label>
                        <input
                            type="text"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Description de la transaction..."
                            className="w-full bg-gray-50 border-0 rounded-2xl px-4 py-3 text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:outline-none"
                        />
                    </div>

                    <div className="flex space-x-4">
                        <button
                            onClick={generatePaymentRequest}
                            disabled={isGenerating || !amount || !phoneNumber}
                            className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 text-white py-4 px-6 rounded-2xl font-semibold shadow-lg hover:from-amber-600 hover:to-amber-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isGenerating ? 'Création...' : 'Envoyer la demande'}
                        </button>
                        <button
                            onClick={resetForm}
                            className="px-6 py-4 bg-gray-100 text-gray-700 rounded-2xl font-semibold hover:bg-gray-200 transition-all"
                        >
                            Effacer
                        </button>
                    </div>
                </div>

                {caissierData?.posId && (
                    <CashierPaymentModal
                        open={showPaymentModal}
                        onClose={() => {
                            setShowPaymentModal(false);
                            resetForm();
                            loadPaymentRequests(); // Recharger la liste après création
                        }}
                        posId={caissierData.posId}
                        cashRegisterId={caissierData.cashRegisterId}
                    />
                )}
            </div>
        );
    }

    // Affichage de la liste (comme dans le mobile)
    return (
        <div className="space-y-6">
            {/* En-tête */}
            <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                        <div className="p-3 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl">
                            <Send className="text-white text-2xl" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">Demandes de Paiement</h2>
                            <p className="text-gray-600">{paymentRequests.length} demande(s)</p>
                        </div>
                    </div>
                    <button
                        onClick={() => setShowCreateForm(true)}
                        className="flex items-center space-x-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-3 rounded-2xl font-semibold hover:from-amber-600 hover:to-amber-700 transition-all shadow-lg"
                    >
                        <Add />
                        <span>Demander un paiement</span>
                    </button>
                </div>

                {/* Filtres de statut */}
                <div className="flex flex-wrap gap-2 mb-4">
                    {['all', 'pending', 'paid', 'cashed', 'expired'].map((status) => (
                        <button
                            key={status}
                            onClick={() => setStatus(status)}
                            className={`px-4 py-2 rounded-xl font-medium transition-all ${
                                sts === status
                                    ? 'bg-amber-500 text-white shadow-lg'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            {getStatusTranslate(status)}
                        </button>
                    ))}
                </div>

                {/* Recherche */}
                <div className="relative mb-6">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Rechercher par numéro ou client..."
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setSlug(e.target.value);
                        }}
                        className="w-full pl-12 pr-4 py-3 bg-gray-50 border-0 rounded-2xl text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:outline-none"
                    />
                </div>
            </div>

            {/* Liste des demandes */}
            <div className="space-y-4">
                {loading ? (
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-amber-500 mx-auto mb-4"></div>
                        <p className="text-gray-600">Chargement...</p>
                    </div>
                ) : paymentRequests.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-3xl shadow-xl border border-gray-100">
                        <div className="text-6xl text-gray-300 mb-4">📱</div>
                        <h3 className="text-lg font-semibold text-gray-500 mb-2">Aucune demande trouvée</h3>
                        <p className="text-gray-400 mb-6">Créez votre première demande de paiement</p>
                        <button
                            onClick={() => setShowCreateForm(true)}
                            className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-3 rounded-2xl font-semibold hover:from-amber-600 hover:to-amber-700 transition-all"
                        >
                            <Add />
                            <span>Créer une demande</span>
                        </button>
                    </div>
                ) : (
                    paymentRequests.map((request) => (
                        <div
                            key={request.id}
                            className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all cursor-pointer"
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <h3 className="text-lg font-bold text-gray-900 mb-1">
                                        {request.title || 'Demande de paiement'}
                                    </h3>
                                    <p className="text-gray-600 mb-1">
                                        {request.method === 'flash'
                                            ? request?.customer?.name || `${request?.paymentMethod?.prefix || ''}${request?.paymentMethod?.account || ''}`
                                            : request?.customer?.name || 'Client non spécifié'}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        {formatDateTime(request.create_at || request.createdAt)}
                                    </p>
                                </div>
                                <div className="text-right ml-4">
                                    <div className="text-xl font-bold text-gray-900 mb-2">
                                        {formatCurrency(request.amount, request.currency)}
                                    </div>
                                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusStyle(request.status)}`}>
                                        {getStatusTranslate(request.status)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Pagination */}
            {paymentRequests.length > 0 && (
                <div className="flex items-center justify-center space-x-4">
                    <button
                        onClick={() => {
                            if (prevPage && prevPage !== pge) {
                                setPage(prevPage);
                            }
                        }}
                        disabled={!prevPage || prevPage === pge}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Précédent
                    </button>
                    <span className="text-gray-600">Page {pge}</span>
                    <button
                        onClick={() => {
                            if (nextPage && nextPage !== pge) {
                                setPage(nextPage);
                            }
                        }}
                        disabled={!nextPage || nextPage === pge}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Suivant
                    </button>
                </div>
            )}
        </div>
    );
};

export default PaymentRequestPanel;
